import React, {useState, useEffect, useCallback} from 'react'
import {Navigate, Route, Routes, Outlet, useParams, useNavigate} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../../_metronic/layout/core'
import {ProfileDetailOverview} from '../../components/profile-detail/ProfileDetailOverview'
import {toast} from 'react-toastify'
import axios from 'axios'
import {ProfileDetailHeader} from '../../components/profile-detail/ProfileDetailHeader'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import RoutesRoleGuard from '../../components/permissions/RoutesPermissionGuard'
import {FaUser} from 'react-icons/fa'
import {useReduxAuth} from '../../../auth'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'
import StaffForm from '../../components/forms/StaffForm'
import apiClient from '../../../../services/api-client'

interface LinkItem {
  title: string
  path: string
  label: string
  allowedRoles: number[]
  icon?: React.ReactNode
}

const NurseDetailsPage: React.FC = () => {
  const notifyError = (msg: string) => toast.error(msg)
  const notifySuccess = (msg: string) => toast.success(msg)

  const navigate = useNavigate()
  const {currentUser} = useReduxAuth()
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined

  const {nurseId} = useParams<{nurseId: string}>()
  const uuid = nurseId ?? ''

  const [userData, setUserData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const fetchDetails = useCallback(async () => {
    setLoading(true)
    try {
      let queryParams: string | undefined
      if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
        queryParams = `doctor_id=${maSelectedDoctorId}`
      }
      
      const res = await axios.get(API_ENDPOINTS.GET_SPECIFIC_STAFF(uuid, queryParams))
      setUserData(res.data.data?.user)
    } catch (err: any) {
      toast.error(err?.response?.data?.message)
      navigate('/unauthorized')
    } finally {
      setLoading(false)
    }
  }, [uuid, navigate, currentUser?.role_id, maSelectedDoctorId])

  useEffect(() => {
    fetchDetails()
  }, [fetchDetails])

  const handleEditPress = () => setIsModalOpen(!isModalOpen)

  const handleSubmit = async (values: any, resetForm: () => void) => {
    if (!uuid) {
      notifyError('Staff UUID not available')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await apiClient.put(API_ENDPOINTS.UPDATE_STAFF(uuid), values)
      notifySuccess(response.data.message || 'Staff updated successfully')
      
      // Update local state with the updated data
      if (response.data.data?.user) {
        setUserData((prev: any) => ({
          ...prev,
          ...response.data.data.user,
          full_name: `${response.data.data.user.first_name} ${response.data.data.user.last_name}`,
        }))
      } else {
        // Fallback: update with submitted values
        setUserData((prev: any) => ({
          ...prev,
          ...values,
          full_name: `${values.first_name} ${values.last_name}`,
        }))
      }
      
      setIsModalOpen(false)
      resetForm()
    } catch (error: any) {
      notifyError(error?.response?.data?.message || 'Failed to update staff')
    } finally {
      setIsSubmitting(false)
    }
  }

  const accountBreadCrumbs: PageLink[] = [
    {title: 'Staff Details', path: `nurse-details/${uuid}`, isSeparator: false, isActive: false},
    {title: '', path: '', isSeparator: true, isActive: false},
  ]

  const links: LinkItem[] = [
    {
      title: 'Overview',
      path: `/nurse-details/${uuid}/overview`,
      label: 'overview',
      allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN, USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT],
      icon: <FaUser />,
    },
  ]

  return (
    <>
      <Routes>
        <Route
          element={
            <>
              {/* <BlurSection
                isSubscribed={
                  currentUser?.role_id == USER_ROLES.ADMIN ||
                  currentUser?.role_id == USER_ROLES.SUB_ADMIN  
                    ? true
                    : currentUser?.subscription_status === 'active'
                }
              > */}
              <ProfileDetailHeader user={userData} uuid={uuid} links={links} isLoading={loading} />
              <Outlet />
              {/* </BlurSection> */}
            </>
          }
        >
          <Route index element={<Navigate to={`/nurse-details/${uuid}/overview`} replace />} />

          <Route
            path='overview'
            element={
              <RoutesRoleGuard
                allowedRoles={[
                  USER_ROLES.ADMIN,
                  USER_ROLES.SUB_ADMIN,
                  USER_ROLES.DOCTOR,
                  USER_ROLES.NURSE,
                  USER_ROLES.MEDICAL_ASSISTANT,
                ]}
              >
                <PageTitle breadcrumbs={accountBreadCrumbs}>Overview</PageTitle>
                <ProfileDetailOverview 
                  user={userData} 
                  onEditPress={handleEditPress}
                  isLoading={loading} 
                />
              </RoutesRoleGuard>
            }
          />

          <Route path='*' element={<Navigate to='/error/404' replace />} />
        </Route>
      </Routes>

      <StaffForm
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        handleSubmit={handleSubmit}
        initialData={userData}
        isLoading={isSubmitting}
      />
    </>
  )
}

export default NurseDetailsPage
